import React from "react";
import { Link } from "react-router-dom";
import {
    Database,
    CalendarClock,
    PhoneCall,
    Headphones,
    UserCheck,
    Globe2,
    Sparkles,
    Megaphone,
    Share2,
    Users,
    Mail,
    BarChart3,
    ArrowRight,
    HelpCircle,
    Phone,
    MapPin,
    Award,
    TrendingUp,
} from "lucide-react";
import CTAButton from "@/components/CTAButton";
import Reveal from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import {
    LINKS,
    IMAGES,
    QUICK_SERVICE_ICONS,
    QUICK_SERVICE_HAS_COUNTRIES,
    PARTNER_LOGOS,
    TEAM_STATIC,
} from "@/lib/content";
import { useT } from "@/lib/i18n";

const iconMap = {
    Database, CalendarClock, PhoneCall, Headphones, UserCheck,
    Globe2, Sparkles, Megaphone, Share2,
};
const serviceIcons = [Database, Mail, PhoneCall, Users, Megaphone, Globe2, BarChart3];

const TILE_CLASS =
    "group relative block h-full p-5 rounded-lg border border-[color:var(--accent)]/50 hover:border-[color:var(--accent)] hover:shadow-[0_12px_40px_-8px_rgba(37,99,235,0.6)] transition-all duration-300";
const TILE_STYLE = {
    background:
        "linear-gradient(135deg, rgba(37,99,235,0.35) 0%, rgba(37,99,235,0.20) 50%, rgba(30,64,175,0.15) 100%)",
};

export default function Home() {
    const { t } = useT();
    const team = TEAM_STATIC.map((s, i) => ({ ...s, ...t.about.team.members[i] }));

    return (
        <main className="pt-[72px] md:pt-[80px]" data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0 -z-10">
                    <video
                        className="w-full h-full object-cover object-center opacity-40"
                        src={IMAGES.heroHomeVideo}
                        poster={IMAGES.heroHome}
                        autoPlay muted loop playsInline preload="metadata"
                        aria-hidden="true" data-testid="hero-video"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--bg-base)]/70 via-[color:var(--bg-base)]/85 to-[color:var(--bg-base)]" />
                    <div className="absolute inset-0 bg-grid opacity-40" />
                </div>

                <div className="container-x pt-24 md:pt-32 pb-20 md:pb-28">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-8">
                            <span className="w-8 h-px bg-[color:var(--accent)]" />
                            <span className="eyebrow" data-testid="hero-eyebrow">{t.hero.eyebrow}</span>
                        </div>
                    </Reveal>

                    <Reveal delay={0.05}>
                        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white max-w-5xl" data-testid="hero-heading">
                            {t.hero.headingA}
                            <br />
                            <span className="text-[color:var(--accent)]">{t.hero.headingB}</span>
                        </h1>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <p className="mt-8 text-white/70 text-lg md:text-xl max-w-2xl leading-relaxed">
                            {t.hero.subheading}
                        </p>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <div className="mt-10 flex flex-col sm:flex-row gap-3">
                            <CTAButton href={LINKS.consultation} external testId="hero-cta-primary">
                                {t.hero.ctaPrimary}
                            </CTAButton>
                            <Link to="/services" className="btn-secondary" data-testid="hero-cta-secondary">
                                <span>{t.hero.ctaSecondary}</span>
                                <ArrowRight size={16} />
                            </Link>
                        </div>
                    </Reveal>

                    <Reveal delay={0.3}>
                        <div className="mt-16 md:mt-24 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-white/10 pt-8">
                            <span className="text-xs uppercase tracking-widest text-white/40 font-semibold">
                                {t.hero.marketsLabel}
                            </span>
                            <div className="flex flex-wrap items-center gap-6">
                                {t.hero.markets.map((c, i) => (
                                    <div key={c.code} className="flex items-center gap-2 text-white/80" data-testid={`hero-country-${c.code.toLowerCase()}`}>
                                        <span className="text-lg" aria-hidden="true">{["🇭🇺","🇸🇰","🇨🇿","🇦🇹"][i]}</span>
                                        <span className="text-sm font-medium tracking-wide">{c.code}</span>
                                        <span className="text-white/40 text-sm hidden md:inline">· {c.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* QUICK SERVICES */}
            <section className="section relative" data-testid="quick-services-section">
                <div className="container-x">
                    <div className="grid md:grid-cols-12 gap-10 items-start">
                        <div className="md:col-span-4">
                            <Reveal>
                                <p className="eyebrow">{t.quickServices.eyebrow}</p>
                                <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
                                    {t.quickServices.heading}
                                </h2>
                                <p className="mt-5 text-white/60 leading-relaxed">
                                    {t.quickServices.subheading}
                                </p>
                            </Reveal>
                        </div>

                        <div className="md:col-span-8">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {t.quickServices.items.map((title, i) => {
                                    const Ico = iconMap[QUICK_SERVICE_ICONS[i]] || Sparkles;
                                    const hasCountries = QUICK_SERVICE_HAS_COUNTRIES[i];
                                    return (
                                        <Reveal key={title} delay={i * 0.03}>
                                            <Link to="/services" className={TILE_CLASS} style={TILE_STYLE} data-testid={`quick-service-${i}`}>
                                                <div className="flex items-start justify-between mb-6">
                                                    <Ico size={22} strokeWidth={1.5} className="text-[color:var(--accent)]" />
                                                    <ArrowRight size={16} className="text-white/30 group-hover:text-white/80 group-hover:translate-x-0.5 transition-all" />
                                                </div>
                                                <div className="text-white font-medium leading-snug">{title}</div>
                                                {hasCountries && (
                                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                                        {["HU","SK","CZ","AT"].map((c) => (
                                                            <span key={c} className="text-[10px] font-semibold tracking-widest text-white/50 border border-white/10 rounded-sm px-1.5 py-0.5">
                                                                {c}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </Link>
                                        </Reveal>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="container-x"><div className="divider" /></div>

            {/* RESULTS */}
            <section className="section" data-testid="results-section">
                <div className="container-x grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5 order-2 md:order-1">
                        <Reveal>
                            <div className="relative aspect-[4/5] overflow-hidden rounded-md shadow-[0_20px_60px_-20px_rgba(37,99,235,0.4)]">
                                <video
                                    className="absolute inset-0 w-full h-full object-cover"
                                    src={IMAGES.resultsVideo} poster={IMAGES.resultsPortrait}
                                    autoPlay muted loop playsInline preload="metadata"
                                    aria-hidden="true" data-testid="results-video"
                                />
                                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                                <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 text-[10px] font-semibold tracking-widest uppercase text-white/85 border border-white/10">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)] animate-pulse" />
                                    SalesShark
                                </div>
                                <div className="absolute inset-0 ring-1 ring-white/10 rounded-md pointer-events-none" />
                            </div>
                        </Reveal>
                    </div>

                    <div className="md:col-span-7 order-1 md:order-2">
                        <Reveal>
                            <p className="eyebrow">{t.results.eyebrow}</p>
                            <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
                                {t.results.heading}
                            </h2>
                        </Reveal>

                        <ul className="mt-10 space-y-6">
                            {t.results.bullets.map((b, i) => (
                                <Reveal key={i} delay={i * 0.05}>
                                    <li className="flex gap-4 items-start">
                                        <span className="mt-2 shrink-0 w-2 h-2 rounded-full bg-[color:var(--accent)]" />
                                        <p className="text-white/80 text-[1.02rem] leading-relaxed">{b}</p>
                                    </li>
                                </Reveal>
                            ))}
                        </ul>

                        <Reveal delay={0.25}>
                            <div className="mt-10 flex flex-col sm:flex-row gap-3">
                                <CTAButton href={LINKS.consultation} external testId="results-cta-consultation">
                                    {t.results.ctaPrimary}
                                </CTAButton>
                                <a href="/#kapcsolat" className="btn-secondary" data-testid="results-cta-contact">
                                    {t.results.ctaSecondary}
                                </a>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* DETAILED SERVICES */}
            <section className="section bg-[color:var(--bg-surface)]/40 border-y border-white/8" data-testid="services-detail-section">
                <div className="container-x">
                    <div className="grid md:grid-cols-12 gap-10 items-start">
                        <div className="md:col-span-4">
                            <Reveal>
                                <p className="eyebrow">{t.detailedServices.eyebrow}</p>
                                <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
                                    {t.detailedServices.heading}
                                </h2>
                                <p className="mt-5 text-white/60 leading-relaxed">
                                    {t.detailedServices.subheading}
                                </p>
                                <Link to="/services" className="btn-secondary mt-8 inline-flex" data-testid="services-detail-cta-more">
                                    <span>{t.detailedServices.ctaMore}</span>
                                    <ArrowRight size={16} />
                                </Link>
                            </Reveal>
                        </div>
                        <div className="md:col-span-8">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {t.detailedServices.items.map((s, i) => {
                                    const Ico = serviceIcons[i] || Sparkles;
                                    const n = String(i + 1).padStart(2, "0");
                                    return (
                                        <Reveal key={s.title} delay={i * 0.04}>
                                            <Link to="/services" className={TILE_CLASS} style={TILE_STYLE} data-testid={`home-service-${i + 1}`}>
                                                <div className="flex items-start justify-between mb-6">
                                                    <div className="flex items-center gap-3">
                                                        <Ico size={22} strokeWidth={1.5} className="text-[color:var(--accent)]" />
                                                        <span className="font-display text-xs text-white/40 tracking-widest">{n}</span>
                                                    </div>
                                                    <ArrowRight size={16} className="text-white/30 group-hover:text-white/80 group-hover:translate-x-0.5 transition-all" />
                                                </div>
                                                <div className="text-white font-medium leading-snug">{s.title}</div>
                                                <p className="mt-2 text-white/55 text-[0.92rem] leading-relaxed">{s.text}</p>
                                            </Link>
                                        </Reveal>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ABOUT */}
            <section id="rolunk" className="section anchor-offset relative overflow-hidden" data-testid="about-section">
                <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full opacity-[0.10] blur-3xl -z-10"
                    style={{ background: "radial-gradient(closest-side, var(--accent), transparent 70%)" }} />
                <div className="container-x">
                    <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-start mb-20">
                        <div className="md:col-span-6">
                            <Reveal>
                                <p className="eyebrow">{t.about.eyebrow}</p>
                                <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-[3.4rem] tracking-tight text-white leading-[1.03]">
                                    {t.about.headingA}
                                    <br />
                                    <span className="text-white/50">{t.about.headingB}</span>
                                    <br />
                                    <span className="text-[color:var(--accent)]">{t.about.headingC}</span>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-6 md:pt-4">
                            <Reveal delay={0.08}>
                                <div className="flex items-center gap-3 mb-5">
                                    <span className="w-8 h-px bg-[color:var(--accent)]" />
                                    <span className="text-xs uppercase tracking-[0.28em] font-semibold text-white/50">
                                        {t.about.story.eyebrow}
                                    </span>
                                </div>
                                <p className="text-white/80 text-[1.05rem] leading-relaxed">{t.about.story.p1}</p>
                                <p className="mt-4 text-white/65 text-[0.98rem] leading-relaxed">{t.about.story.p2}</p>
                                <p className="mt-4 text-white/65 text-[0.98rem] leading-relaxed">{t.about.story.p3}</p>
                            </Reveal>
                        </div>
                    </div>

                    <Reveal>
                        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-b border-white/10 divide-x divide-white/8">
                            {t.about.stats.map((s, i) => (
                                <div key={i} className="py-8 md:py-10 px-4 md:px-6" data-testid={`about-stat-${i}`}>
                                    <div className="font-display text-4xl md:text-5xl lg:text-[3.25rem] text-white tracking-tight leading-none">{s.n}</div>
                                    <div className="mt-3 text-white/50 text-[0.82rem] uppercase tracking-widest font-medium">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </Reveal>

                    <div className="mt-20 grid md:grid-cols-12 gap-10 lg:gap-14">
                        <div className="md:col-span-4">
                            <Reveal>
                                <p className="eyebrow">{t.about.pillars.eyebrow}</p>
                                <h3 className="mt-3 font-display text-2xl md:text-3xl text-white tracking-tight leading-snug">
                                    {t.about.pillars.headingA}
                                    <span className="text-[color:var(--accent)]"> {t.about.pillars.headingB}</span>
                                </h3>
                            </Reveal>
                        </div>
                        <div className="md:col-span-8">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                                {t.about.pillars.items.map((v, i) => {
                                    const icons = [BarChart3, Award, Users, Globe2];
                                    const Ico = icons[i] || Sparkles;
                                    return (
                                        <Reveal key={v.title} delay={0.05 + i * 0.05}>
                                            <div className="py-6 border-b border-white/8 flex gap-4" data-testid={`about-pillar-${i}`}>
                                                <div className="shrink-0 w-11 h-11 rounded-md border border-white/10 bg-white/[0.02] flex items-center justify-center">
                                                    <Ico size={20} strokeWidth={1.5} className="text-[color:var(--accent)]" />
                                                </div>
                                                <div>
                                                    <div className="font-display text-white text-lg leading-snug">{v.title}</div>
                                                    <p className="mt-1.5 text-white/60 text-[0.94rem] leading-relaxed">{v.text}</p>
                                                </div>
                                            </div>
                                        </Reveal>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="mt-24 md:mt-32 mb-10">
                        <Reveal>
                            <p className="eyebrow">{t.about.team.eyebrow}</p>
                            <h3 className="mt-3 font-display text-3xl md:text-4xl tracking-tight text-white leading-tight">
                                {t.about.team.heading}
                            </h3>
                        </Reveal>
                    </div>

                    <div className="space-y-6 lg:space-y-8">
                        {team.map((m, i) => (
                            <Reveal key={m.name} delay={i * 0.08}>
                                <article className={`grid md:grid-cols-12 gap-6 md:gap-10 items-stretch rounded-lg border border-white/10 bg-[color:var(--bg-surface)]/60 overflow-hidden ${i % 2 === 1 ? "md:[&>.photo]:order-2" : ""}`} data-testid={`team-member-${i}`}>
                                    <div className="photo md:col-span-4 relative">
                                        <div className="aspect-[3/4] md:aspect-auto md:h-full overflow-hidden">
                                            <img src={m.image} alt={m.name} className="w-full h-full object-cover object-center" loading="lazy" />
                                        </div>
                                        <div className="absolute top-4 left-4 text-white/70 text-xs font-display tracking-widest bg-black/50 backdrop-blur px-2.5 py-1 rounded">
                                            0{i + 1}
                                        </div>
                                    </div>
                                    <div className="md:col-span-8 p-6 md:p-8 lg:p-10 flex flex-col">
                                        <div className="text-[color:var(--accent)] text-xs uppercase tracking-[0.28em] font-semibold">{m.role}</div>
                                        <h4 className="mt-2 font-display text-2xl md:text-3xl lg:text-[2rem] text-white tracking-tight leading-tight">{m.name}</h4>
                                        <div className="mt-6 space-y-3.5 flex-1 max-w-3xl">
                                            {m.bio.map((p, k) => (
                                                <p key={k} className={`text-[0.98rem] leading-relaxed ${k === 0 ? "text-white/85" : "text-white/60"}`}>{p}</p>
                                            ))}
                                        </div>
                                        <div className="mt-7 pt-6 border-t border-white/8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 justify-between">
                                            <div className="flex flex-wrap gap-1.5">
                                                {m.languages.map((l) => (
                                                    <span key={l} className="text-[10px] font-semibold tracking-widest text-white/70 border border-white/15 rounded-sm px-2 py-1">{l}</span>
                                                ))}
                                            </div>
                                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 text-sm">
                                                <a href={`mailto:${m.email}`} className="inline-flex items-center gap-2 text-white/85 hover:text-[color:var(--accent)] transition-colors" data-testid={`team-email-${i}`}>
                                                    <Mail size={14} strokeWidth={1.5} />{m.email}
                                                </a>
                                                <a href={m.phoneHref} className="inline-flex items-center gap-2 text-white/85 hover:text-[color:var(--accent)] transition-colors" data-testid={`team-phone-${i}`}>
                                                    <Phone size={14} strokeWidth={1.5} />{m.phone}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROBLEMS */}
            <section className="section" data-testid="problems-section">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <Reveal>
                            <p className="eyebrow">{t.problems.eyebrow}</p>
                            <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
                                {t.problems.heading}
                            </h2>
                            <p className="mt-5 text-white/60 leading-relaxed">{t.problems.subheading}</p>
                        </Reveal>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {t.problems.items.map((p, i) => (
                            <Reveal key={i} delay={i * 0.03}>
                                <div className="flex gap-4 items-start p-5 md:p-6 rounded-md border border-white/8 bg-white/[0.015] hover:border-white/20 hover:bg-white/[0.03] transition-colors" data-testid={`problem-${i}`}>
                                    <HelpCircle size={20} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[color:var(--accent)]" />
                                    <p className="text-white/85 leading-relaxed text-[0.98rem]">{p}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.2}>
                        <div className="mt-14 flex justify-center">
                            <CTAButton href={LINKS.consultation} external testId="problems-cta-consultation">
                                {t.problems.cta}
                            </CTAButton>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* REFERENCES */}
            <section id="referenciak" className="section anchor-offset relative border-t border-white/8" data-testid="references-section">
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                    <div className="absolute top-24 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full opacity-[0.12] blur-3xl"
                        style={{ background: "radial-gradient(closest-side, var(--accent), transparent 70%)" }} />
                </div>
                <div className="container-x">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="eyebrow">{t.references.eyebrow}</p>
                                <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
                                    {t.references.headingA}
                                    <br />
                                    <span className="text-white/50">{t.references.headingB}</span>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-4 md:text-right">
                            <Reveal delay={0.1}>
                                <div className="inline-flex items-center gap-3 text-white/60 text-sm">
                                    <Award size={18} strokeWidth={1.5} className="text-[color:var(--accent)]" />
                                    <span>{t.references.statBadge}</span>
                                </div>
                            </Reveal>
                        </div>
                    </div>

                    <Reveal>
                        <div className="grid grid-cols-3 gap-4 md:gap-6 mb-10">
                            {PARTNER_LOGOS.map((p, i) => (
                                <div key={p.name} className="group flex items-center justify-center h-24 md:h-28 rounded-md bg-white/[0.96] hover:bg-white transition-colors px-6 shadow-[0_8px_30px_-12px_rgba(37,99,235,0.35)]" data-testid={`partner-logo-${i}`}>
                                    <img src={p.src} alt={p.name} className="max-h-14 md:max-h-16 max-w-full object-contain" loading="lazy" />
                                </div>
                            ))}
                        </div>
                    </Reveal>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {t.references.items.map((r, i) => (
                            <Reveal key={r.company} delay={i * 0.06}>
                                <figure className="relative h-full p-7 md:p-8 border border-white/10 rounded-lg bg-[color:var(--bg-surface)]/70 flex flex-col overflow-hidden" data-testid={`reference-${i}`}>
                                    <div aria-hidden="true" className="absolute -top-8 -right-4 text-[8rem] leading-none font-display font-bold text-white/[0.04] select-none">“</div>
                                    <div className="mb-6 h-10 flex items-center bg-white/95 rounded-md px-3 py-1 self-start">
                                        <img src={PARTNER_LOGOS[i].src} alt={r.company} className="max-h-8 max-w-[120px] object-contain" loading="lazy" />
                                    </div>
                                    <blockquote className="text-white/85 text-[0.98rem] leading-relaxed flex-1">{r.text}</blockquote>
                                    <figcaption className="mt-6 pt-5 border-t border-white/8 flex items-center justify-between">
                                        <div>
                                            <div className="text-white font-display font-semibold tracking-tight">{r.company}</div>
                                            <div className="text-white/50 text-[0.7rem] uppercase tracking-widest mt-0.5">{r.role}</div>
                                        </div>
                                        <span className="inline-flex items-center gap-1 text-[color:var(--accent)] text-xs font-semibold">
                                            <TrendingUp size={14} strokeWidth={2} />
                                            {t.references.verifiedBadge}
                                        </span>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* GOAL */}
            <section className="section" data-testid="goal-section">
                <div className="container-x">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-lg border border-white/10 bg-[color:var(--bg-surface)]">
                            <div className="grid md:grid-cols-2 gap-0">
                                <div className="p-8 md:p-14 lg:p-16">
                                    <p className="eyebrow">{t.goal.eyebrow}</p>
                                    <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-[2.6rem] tracking-tight text-white leading-tight">
                                        {t.goal.heading}
                                    </h2>
                                    <p className="mt-6 text-white/70 leading-relaxed text-[1.02rem]">{t.goal.p1}</p>
                                    <p className="mt-4 text-white/70 leading-relaxed text-[1.02rem]">{t.goal.p2}</p>
                                    <div className="mt-9 flex flex-col sm:flex-row gap-3">
                                        <a href="/#kapcsolat" className="btn-primary" data-testid="goal-cta-contact">{t.goal.ctaContact}</a>
                                        <a href={LINKS.companyPdf} target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="goal-cta-presentation">
                                            {t.goal.ctaPresentation}
                                        </a>
                                    </div>
                                </div>
                                <div className="relative min-h-[280px] md:min-h-full">
                                    <img src={IMAGES.goal} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                    <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--bg-surface)] via-transparent to-transparent md:from-[color:var(--bg-surface)]/60" />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CONTACT */}
            <section id="kapcsolat" className="section anchor-offset border-t border-white/8 bg-[color:var(--bg-surface)]/30" data-testid="contact-section">
                <div className="container-x">
                    <div className="grid md:grid-cols-12 gap-10 mb-14 items-end">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="eyebrow">{t.contact.eyebrow}</p>
                                <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
                                    {t.contact.headingA}
                                    <br />
                                    <span className="text-white/50">{t.contact.headingB}</span>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-4 md:text-right">
                            <Reveal delay={0.1}>
                                <CTAButton href={LINKS.consultation} external testId="contact-cta-consultation">
                                    {t.contact.ctaConsultation}
                                </CTAButton>
                            </Reveal>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {team.map((m, i) => (
                                <Reveal key={m.email} delay={i * 0.06}>
                                    <div className="h-full p-6 rounded-lg border border-white/10 bg-[color:var(--bg-base)]/60" data-testid={`contact-card-${i}`}>
                                        <div className="text-[color:var(--accent)] text-xs uppercase tracking-widest font-semibold">{m.role}</div>
                                        <div className="mt-2 font-display text-xl text-white tracking-tight">{m.name}</div>
                                        <div className="mt-5 space-y-2">
                                            <a href={`mailto:${m.email}`} className="inline-flex items-center gap-2 text-white/90 hover:text-[color:var(--accent)] transition-colors text-[0.98rem]" data-testid={`contact-email-${i}`}>
                                                <Mail size={16} strokeWidth={1.5} />{m.email}
                                            </a>
                                            <br />
                                            <a href={m.phoneHref} className="inline-flex items-center gap-2 text-white/90 hover:text-[color:var(--accent)] transition-colors text-[0.98rem]" data-testid={`contact-phone-${i}`}>
                                                <Phone size={16} strokeWidth={1.5} />{m.phone}
                                            </a>
                                        </div>
                                        <div className="mt-5 pt-5 border-t border-white/8 flex flex-wrap gap-1.5">
                                            {m.languages.map((l) => (
                                                <span key={l} className="text-[10px] font-semibold tracking-widest text-white/60 border border-white/12 rounded-sm px-1.5 py-0.5">{l}</span>
                                            ))}
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>

                        <div className="lg:col-span-5">
                            <Reveal delay={0.15}>
                                <div className="h-full p-6 md:p-8 rounded-lg border border-white/10 bg-[color:var(--bg-base)]/60 flex flex-col">
                                    <div className="text-[color:var(--accent)] text-xs uppercase tracking-widest font-semibold">{t.contact.officeTitle}</div>
                                    <div className="mt-2 font-display text-xl text-white tracking-tight">{t.contact.officeSubtitle}</div>
                                    <ul className="mt-6 space-y-3">
                                        {t.contact.offices.map((o, i) => (
                                            <li key={i} className="flex items-center gap-3 text-white/85 text-[0.95rem]" data-testid={`office-${i}`}>
                                                <span className="text-lg" aria-hidden="true">{o.country === "HU" ? "🇭🇺" : "🇸🇰"}</span>
                                                <span className="text-white/60 text-xs font-semibold tracking-widest w-6">{o.country}</span>
                                                <MapPin size={14} strokeWidth={1.5} className="text-white/40" />
                                                <span>{o.city}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-8 pt-6 border-t border-white/8">
                                        <div className="text-white font-medium">{t.contact.company.name}</div>
                                        <div className="mt-1 text-white/60 text-sm">
                                            {t.contact.company.addressLine1}
                                            <br />
                                            {t.contact.company.addressLine2}
                                        </div>
                                        <div className="mt-2 text-white/40 text-xs">{t.contact.company.id}</div>
                                    </div>
                                </div>
                            </Reveal>
                        </div>
                    </div>

                    <div className="mt-8 lg:mt-10">
                        <Reveal delay={0.05}>
                            <ContactForm />
                        </Reveal>
                    </div>
                </div>
            </section>
        </main>
    );
}
